import type { components } from '@connected/api-client/api.generated';
import type { ButtonProps } from '@connected/ui/components/button';

import { createContext, use, useMemo } from 'react';
import { toast } from 'sonner';

import { useObjectObservations } from '@/hooks/queries/use-observations';

import type { CheckListItemKinds, ObservationObjectKinds, ObservationParentKinds } from '../utils/observation-types';
import type { ObservationTableProps } from './observation-table';

import { getObservationParentKind } from '../utils/observation-text-mapping';
import { CreateRemark } from './observation-create-modal';
import { ObservationTable } from './observation-table';

// ============================================================================
// Types
// ============================================================================

export type ObservationProps<ObjectKind extends ObservationObjectKinds> = ObjectKind extends 'CheckListItem' ? {
    electricalProjectVersionId: string;
    objectId: string;
    objectKind: ObjectKind;
    checkListItemKind: CheckListItemKinds;
} : {
    electricalProjectVersionId: string;
    objectId: string;
    objectKind: ObjectKind;
};

type ObservationContextValue = {
    electricalProjectVersionId: string;
    objectId: string;
    objectKind: ObservationObjectKinds;
    parentKind: ObservationParentKinds;
    observations: components['schemas']['ListElectricalProjectObservationQueryResultObservation'][];
    count: number;
    isLoading: boolean;
    isError: boolean;
};

// ============================================================================
// Context
// ============================================================================

const ObservationContext = createContext<ObservationContextValue | null>(null);

function useObservationContext() {
    const context = use(ObservationContext);
    if (!context) {
        throw new Error('Observation compound components must be used within an Observation.Root');
    }
    return context;
}

// ============================================================================
// Root Component (Provider)
// ============================================================================

type ObservationRootProps<ObjectKind extends ObservationObjectKinds> = ObservationProps<ObjectKind> & {
    children: React.ReactNode;
};

function ObservationRoot<ObjectKind extends ObservationObjectKinds>(props: ObservationRootProps<ObjectKind>) {
    const { electricalProjectVersionId, objectId, objectKind, children } = props;

    const parentKind = useMemo(() => getObservationParentKind(props), [props]);
    const observationList = useObjectObservations(props);

    const observations = useMemo<components['schemas']['ListElectricalProjectObservationQueryResultObservation'][]>(
        () => {
            if (observationList.isError) {
                toast.error(observationList.error?.Message);
                return [];
            }

            if (!observationList.data) {
                return [];
            }

            return observationList.data.observations;
        },
        [observationList.isError, observationList.data, observationList.error?.Message],
    );

    const contextValue = useMemo<ObservationContextValue>(() => ({
        electricalProjectVersionId,
        objectId,
        objectKind,
        parentKind,
        observations,
        count: observationList.data?.count || 0,
        isLoading: observationList.isLoading,
        isError: observationList.isError,
    }), [
        electricalProjectVersionId,
        objectId,
        objectKind,
        parentKind,
        observations,
        observationList.data?.count,
        observationList.isLoading,
        observationList.isError,
    ]);

    return (
        <ObservationContext value={contextValue}>
            {children}
        </ObservationContext>
    );
}

// ============================================================================
// Table Component (Context Wrapper)
// ============================================================================

type ObservationTableWrapperProps = Omit<ObservationTableProps, 'electricalProjectVersionId' | 'objectKind' | 'parentKind' | 'observations' | 'isLoading'>;

function ObservationTableWrapper({ className }: ObservationTableWrapperProps) {
    const {
        electricalProjectVersionId,
        objectKind,
        parentKind,
        observations,
        isLoading,
    } = useObservationContext();

    return (
        <ObservationTable
            electricalProjectVersionId={electricalProjectVersionId}
            objectKind={objectKind}
            parentKind={parentKind}
            observations={observations}
            isLoading={isLoading}
            className={className}
        />
    );
}

// ============================================================================
// Create Button Component
// ============================================================================

export type ObservationCreateButtonProps = ButtonProps;

function ObservationCreateButton({ className, ...props }: ObservationCreateButtonProps) {
    const {
        electricalProjectVersionId,
        objectId,
        objectKind,
        parentKind,
        count,
    } = useObservationContext();

    return (
        <CreateRemark
            electricalProjectVersionId={electricalProjectVersionId}
            objectId={objectId}
            objectKind={objectKind}
            parentKind={parentKind}
            count={count}
            className={className}
            {...props}
        />
    );
}

// ============================================================================
// Compound Component Export
// ============================================================================

export const Observation = Object.assign(ObservationRoot, {
    Root: ObservationRoot,
    Table: ObservationTableWrapper,
    CreateButton: ObservationCreateButton,
});

// ============================================================================
// Deprecated Component (for backwards compatibility)
// ============================================================================

export type ObservationComponentProps<ObjectKind extends ObservationObjectKinds> = ObservationProps<ObjectKind>;

/**
 * @deprecated Use the `Observation` compound component directly instead.
 * This component is deprecated and will be removed in the future.
 *
 * @example
 * ```tsx
 * <Observation.Root electricalProjectVersionId={id} objectId={objectId} objectKind={kind}>
 *   <Observation.Table />
 *   <Observation.CreateButton />
 * </Observation.Root>
 * ```
 */
export function ObservationComponent<ObjectKind extends ObservationObjectKinds>(props: ObservationComponentProps<ObjectKind>) {
    return (
        <Observation.Root {...props}>
            <div className="w-full flex flex-col items-center gap-4">
                <Observation.Table />
                <Observation.CreateButton />
            </div>
        </Observation.Root>
    );
}
